import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ShoppingBag, Plus, Camera, Image as ImageIcon, X, MoreVertical, Copy, ArrowUp, ArrowDown, Trash2, RotateCcw,
  Mail, Pencil, Check, Inbox,
} from 'lucide-react';
import { KitPhoto } from '../kitswap/KitSwap';
import { shrinkPhoto } from '../kitswap/kitPhoto';
import { formatGBP } from '../../lib/money';
import '../kitswap/KitSwap.css';
import './ShopManager.css';

/**
 * Shop manager (B-49 slice 1, v47) — Club Admin → Club → Shop.
 *
 * Admins add, edit, show / hide, duplicate, reorder and delete shop items
 * (delete = out of the shop at once, restorable for 30 days), with up to 3
 * photos each, shrunk on the phone exactly like Kit Swap's (kitPhoto.js) and
 * the same "no people in shot" tick. The form follows Kit Swap's "List an
 * item". Items: Show in shop toggle, Edit, ⋮ (Duplicate · Move up/down ·
 * Delete).
 *
 * Also here: the kit-contact email ("Ask about this" enquiries are emailed
 * to it; no email = the button is hidden in the Shop), and the Enquiries
 * list with one-tap status: New → Reserved → Sold (amount, prefilled price ×
 * quantity) or Closed.
 *
 * API: /clubs/:slug/shop/admin… (routes/shopManager.js). Bulk import, export,
 * checkout and the Shop report are later slices.
 */

export const CATEGORIES = [
  { key: 'kit', label: 'Match Kit' },
  { key: 'training', label: 'Training' },
  { key: 'casual', label: 'Casual Wear' },
  { key: 'accessories', label: 'Accessories' },
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map(c => [c.key, c.label]));
const ADULT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const JUNIOR_SIZES = ['Age 5–6', 'Age 7–8', 'Age 9–10', 'Age 11–12', 'Age 13–14'];
const NAME_MAX = 80;
const DESCRIPTION_MAX = 1000;
const MAX_PHOTOS = 3;

const errText = (err, fallback) => err?.response?.data?.message || err?.response?.data?.error || fallback;
const priceText = (p) => formatGBP(p, 'Price on request');
const penceText = (p) => formatGBP(Number(p || 0) / 100);
const MAX_AMOUNT_PENCE = 10000000; // £100,000 — the server's typo guard
/** "45", "£45.00", "45.5" → 4550 pence; anything else → null. */
const toPence = (s) => {
  const t = String(s ?? '').trim().replace(/^£\s*/, '').replace(/,/g, '');
  if (!/^\d{1,6}(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 100);
};
const shortDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '');

// ─────────────────────────────────────────────────────────────────────
// Add / edit an item
// ─────────────────────────────────────────────────────────────────────

/** A photo slot in the form: one already on the item, or a new shrunk blob. */
function FormThumb({ photo, onRemove }) {
  return (
    <div className="shopm-thumb">
      {photo.kind === 'existing'
        ? <KitPhoto url={photo.url} alt="" className="shopm-thumb-img" Placeholder={ShoppingBag} />
        : <img src={photo.preview} alt="" className="shopm-thumb-img" />}
      <button type="button" className="shopm-thumb-x" aria-label="Remove photo" onClick={onRemove}><X size={14} /></button>
    </div>
  );
}

function ItemForm({ clubSlug, item, onDone, onCancel }) {
  const isEdit = !!item;
  const takeRef = useRef(null);
  const pickRef = useRef(null);
  const [photos, setPhotos] = useState(() => (item?.photos || []).map(p => ({ kind: 'existing', id: p.id, url: p.url })));
  const [shrinking, setShrinking] = useState(false);
  const [name, setName] = useState(item?.name || '');
  const [category, setCategory] = useState(item?.category || '');
  const [price, setPrice] = useState(item?.price != null ? Number(item.price).toFixed(2) : '');
  // New items start with the usual adult sizes picked (spec): one tap off.
  const [sizes, setSizes] = useState(() => (item ? item.sizes || [] : ADULT_SIZES.slice()));
  const [showJunior, setShowJunior] = useState(() => (item?.sizes || []).some(s => JUNIOR_SIZES.includes(s)));
  const [customSize, setCustomSize] = useState('');
  const [colours, setColours] = useState(item?.colours || []);
  const [colourText, setColourText] = useState('');
  const [description, setDescription] = useState(item?.description || '');
  const [customisable, setCustomisable] = useState(!!item?.is_customisable);
  const [itemCode, setItemCode] = useState(item?.item_code || '');
  const [visible, setVisible] = useState(item ? item.is_available !== false : true);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Revoke the object URLs of new photos when they go away.
  const previewsRef = useRef([]);
  useEffect(() => {
    const urls = previewsRef.current; // the same array for the form's life
    return () => urls.forEach(u => URL.revokeObjectURL(u));
  }, []);

  const hasNewPhotos = photos.some(p => p.kind === 'new');

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setError('');
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) { setError(`Up to ${MAX_PHOTOS} photos per item.`); return; }
    setShrinking(true);
    try {
      const added = [];
      for (const f of files.slice(0, room)) {
        const blob = await shrinkPhoto(f);
        const preview = URL.createObjectURL(blob);
        previewsRef.current.push(preview);
        added.push({ kind: 'new', key: preview, blob, preview });
      }
      setPhotos(ps => [...ps, ...added].slice(0, MAX_PHOTOS));
      if (files.length > room) setError(`Up to ${MAX_PHOTOS} photos per item — we added the first ${room}.`);
    } catch (err) {
      setError(err.message || 'Could not read that photo.');
    } finally { setShrinking(false); }
  };

  const toggleSize = (s) => setSizes(cur => (cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s]));
  const addCustomSize = () => {
    const s = customSize.replace(/\s+/g, ' ').trim();
    if (!s) return;
    if (s.length > 30) { setError('Keep each size under 30 characters.'); return; }
    if (!sizes.some(x => x.toLowerCase() === s.toLowerCase())) setSizes(cur => [...cur, s]);
    setCustomSize('');
  };
  const addColour = () => {
    const c = colourText.replace(/\s+/g, ' ').trim();
    if (!c) return;
    if (c.length > 30) { setError('Keep each colour under 30 characters.'); return; }
    if (!colours.some(x => x.toLowerCase() === c.toLowerCase())) setColours(cur => [...cur, c]);
    setColourText('');
  };
  const customSizes = sizes.filter(s => !ADULT_SIZES.includes(s) && !JUNIOR_SIZES.includes(s));
  // Sizes go to the server in the order they're offered: adult, junior, then the club's own.
  const orderedSizes = [...ADULT_SIZES, ...JUNIOR_SIZES].filter(s => sizes.includes(s)).concat(customSizes);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const cleanName = name.replace(/\s+/g, ' ').trim();
    if (cleanName.length < 2) return setError('Give the item a name, e.g. Home shirt.');
    if (cleanName.length > NAME_MAX) return setError(`Keep the name to ${NAME_MAX} characters.`);
    if (!category) return setError('Choose a category.');
    const p = price.trim().replace(/^£\s*/, '');
    if (p && !/^\d{1,4}(\.\d{1,2})?$/.test(p)) return setError('Enter the price in pounds, e.g. 25 or 24.99 — or leave it blank for "Price on request".');
    if (hasNewPhotos && !confirmed) return setError('Tick to confirm the photos show the kit only, no people.');
    // Anything still typed in the add boxes counts.
    const finalColours = colourText.trim() && !colours.some(x => x.toLowerCase() === colourText.trim().toLowerCase())
      ? [...colours, colourText.replace(/\s+/g, ' ').trim()] : colours;
    const finalSizes = customSize.trim() && !orderedSizes.some(x => x.toLowerCase() === customSize.trim().toLowerCase())
      ? [...orderedSizes, customSize.replace(/\s+/g, ' ').trim()] : orderedSizes;

    const fd = new FormData();
    fd.append('name', cleanName);
    fd.append('category', category);
    fd.append('price', p);
    fd.append('sizes', JSON.stringify(finalSizes));
    fd.append('colours', JSON.stringify(finalColours));
    fd.append('description', description.trim());
    fd.append('is_available', String(visible));
    fd.append('is_customisable', String(customisable));
    fd.append('item_code', itemCode.trim());
    if (isEdit) fd.append('keep_photos', JSON.stringify(photos.filter(x => x.kind === 'existing').map(x => x.id)));
    if (hasNewPhotos) fd.append('confirm_no_people', 'true');
    photos.filter(x => x.kind === 'new').forEach((x, i) => fd.append('photos', x.blob, `item-${i + 1}.jpg`));

    setSaving(true);
    try {
      if (isEdit) await axios.patch(`/clubs/${clubSlug}/shop/admin/items/${item.id}`, fd);
      else await axios.post(`/clubs/${clubSlug}/shop/admin/items`, fd);
      onDone(isEdit ? 'Saved.' : (visible ? 'Added — it\'s in the shop now.' : 'Added as hidden — switch on Show in shop when it\'s ready.'));
    } catch (err) {
      setError(errText(err, 'Could not save the item — try again.'));
    } finally { setSaving(false); }
  };

  return (
    <form className="kit-form card shopm-form" onSubmit={submit}>
      <div className="kit-form-head">
        <h3>{isEdit ? 'Edit item' : 'Add an item'}</h3>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}><X size={14} /> Cancel</button>
      </div>

      <div className="form-group">
        <label className="label">Photos <span className="optional">(optional, up to {MAX_PHOTOS})</span></label>
        {photos.length > 0 && (
          <div className="shopm-thumbs">
            {photos.map((ph, i) => (
              <FormThumb key={ph.kind === 'existing' ? ph.id : ph.key} photo={ph}
                onRemove={() => setPhotos(ps => ps.filter((_, j) => j !== i))} />
            ))}
          </div>
        )}
        {photos.length < MAX_PHOTOS && (
          <div className="kit-form-photo-btns">
            <button type="button" className="btn btn-ghost" onClick={() => takeRef.current?.click()} disabled={shrinking}><Camera size={16} /> Take a photo</button>
            <button type="button" className="btn btn-ghost" onClick={() => pickRef.current?.click()} disabled={shrinking}><ImageIcon size={16} /> Choose photos</button>
          </div>
        )}
        {shrinking && <p className="kit-hint">Getting your photos ready…</p>}
        {photos.length > 1 && <p className="kit-hint">The first photo is the one members see on the shop page.</p>}
        <input ref={takeRef} type="file" accept="image/*" capture="environment" hidden onChange={onFile} />
        <input ref={pickRef} type="file" accept="image/*" multiple hidden onChange={onFile} />
      </div>

      <div className="form-group">
        <label className="label" htmlFor="shopm-name">Name</label>
        <input id="shopm-name" className="input" type="text" aria-required="true" maxLength={NAME_MAX}
          autoComplete="off" enterKeyHint="next" placeholder="e.g. Home shirt"
          value={name} onChange={e => setName(e.target.value)} />
      </div>

      <div className="form-group">
        <label className="label" htmlFor="shopm-price">Price</label>
        <div className="shopm-price">
          <span aria-hidden="true">£</span>
          <input id="shopm-price" className="input" type="text" inputMode="decimal" autoComplete="off"
            placeholder="Price on request" value={price} onChange={e => setPrice(e.target.value)} />
        </div>
        <p className="kit-hint">Leave blank and members see “Price on request”.</p>
      </div>

      <div className="form-group">
        <label className="label">Category</label>
        <div className="pill-group">
          {CATEGORIES.map(c => (
            <button type="button" key={c.key} className={`pill-btn ${category === c.key ? 'active' : ''}`} onClick={() => setCategory(c.key)}>{c.label}</button>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label className="label">Sizes <span className="optional">(tap to add or remove)</span></label>
        <div className="pill-group shopm-chips">
          {ADULT_SIZES.map(s => (
            <button type="button" key={s} aria-pressed={sizes.includes(s)} className={`pill-btn ${sizes.includes(s) ? 'active' : ''}`} onClick={() => toggleSize(s)}>{s}</button>
          ))}
          {!showJunior && (
            <button type="button" className="pill-btn shopm-chip-more" onClick={() => setShowJunior(true)}><Plus size={12} /> Junior sizes</button>
          )}
        </div>
        {showJunior && (
          <div className="pill-group shopm-chips">
            {JUNIOR_SIZES.map(s => (
              <button type="button" key={s} aria-pressed={sizes.includes(s)} className={`pill-btn ${sizes.includes(s) ? 'active' : ''}`} onClick={() => toggleSize(s)}>{s}</button>
            ))}
          </div>
        )}
        {customSizes.length > 0 && (
          <div className="pill-group shopm-chips">
            {customSizes.map(s => (
              <button type="button" key={s} className="pill-btn active" onClick={() => toggleSize(s)} aria-label={`Remove size ${s}`}>{s} <X size={12} /></button>
            ))}
          </div>
        )}
        <div className="shopm-add-row">
          <input className="input" maxLength={30} placeholder="Another size, e.g. One size" value={customSize}
            onChange={e => setCustomSize(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomSize(); } }} />
          <button type="button" className="btn btn-ghost btn-sm" onClick={addCustomSize}>Add</button>
        </div>
        {sizes.length === 0 && <p className="kit-hint">No sizes — members won't be asked for one.</p>}
      </div>

      <div className="form-group">
        <label className="label">Colours <span className="optional">(optional)</span></label>
        {colours.length > 0 && (
          <div className="pill-group shopm-chips">
            {colours.map(c => (
              <button type="button" key={c} className="pill-btn active" onClick={() => setColours(cur => cur.filter(x => x !== c))} aria-label={`Remove colour ${c}`}>{c} <X size={12} /></button>
            ))}
          </div>
        )}
        <div className="shopm-add-row">
          <input className="input" maxLength={30} placeholder="e.g. Green and gold" value={colourText}
            onChange={e => setColourText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addColour(); } }} />
          <button type="button" className="btn btn-ghost btn-sm" onClick={addColour}>Add</button>
        </div>
      </div>

      <div className="form-group">
        <label className="label" htmlFor="shopm-desc">Description <span className="optional">(optional)</span></label>
        <textarea id="shopm-desc" className="input" rows={3} maxLength={DESCRIPTION_MAX} value={description}
          placeholder="e.g. 2026 home shirt, club crest and sponsor on the front" onChange={e => setDescription(e.target.value)} />
        <p className="kit-hint" style={{ textAlign: 'right' }}>{description.length}/{DESCRIPTION_MAX}</p>
      </div>

      <div className="form-group">
        <label className="label" htmlFor="shopm-code">Item code <span className="optional">(optional — your own reference)</span></label>
        <input id="shopm-code" className="input" maxLength={40} autoComplete="off" placeholder="e.g. HS-26" value={itemCode} onChange={e => setItemCode(e.target.value)} />
      </div>

      <label className="kit-tick">
        <input type="checkbox" checked={customisable} onChange={e => setCustomisable(e.target.checked)} />
        <span>Can be personalised (name / number)</span>
      </label>
      <label className="kit-tick">
        <input type="checkbox" checked={visible} onChange={e => setVisible(e.target.checked)} />
        <span>Show in shop</span>
      </label>
      {hasNewPhotos && (
        <label className="kit-tick">
          <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
          <span>Photos show the kit only, no people</span>
        </label>
      )}

      {error && <div className="kit-error">{error}</div>}
      <button type="submit" className="btn btn-primary kit-wide" disabled={saving || shrinking}>
        {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Add to shop')}
      </button>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Kit contact (where enquiries go)
// ─────────────────────────────────────────────────────────────────────

function KitContactCard({ clubSlug, email, emailsOn, onSaved }) {
  const [editing, setEditing] = useState(!email);
  const [value, setValue] = useState(email || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (!editing) setValue(email || ''); }, [email, editing]);

  const save = async (e) => {
    e?.preventDefault();
    setError('');
    const v = value.trim();
    if (v && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) { setError('That doesn\'t look like an email address.'); return; }
    if (!v && email && !window.confirm('Remove the kit contact? Members won\'t be able to ask about items until you add one again.')) return;
    setSaving(true);
    try {
      await axios.patch(`/clubs/${clubSlug}/shop/admin/settings`, { contact_email: v });
      setEditing(!v);
      onSaved();
    } catch (err) {
      setError(errText(err, 'Could not save the kit contact.'));
    } finally { setSaving(false); }
  };

  if (!editing && email) {
    return (
      <div className="shopm-contact card">
        <Mail size={18} className="shopm-contact-icon" />
        <div className="shopm-contact-main">
          <strong>Enquiries go to {email}</strong>
          <p className="kit-hint">
            {emailsOn
              ? <>Members tap <strong>Ask about this</strong> on an item; the enquiry is emailed here and listed under Enquiries.</>
              : <>Members can tap <strong>Ask about this</strong> now and you'll find their enquiries under Enquiries. Enquiries are emailed once Marakin has verified your club.</>}
          </p>
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>Change</button>
      </div>
    );
  }
  return (
    <form className="shopm-contact card" onSubmit={save}>
      <Mail size={18} className="shopm-contact-icon" />
      <div className="shopm-contact-main">
        <label className="label" htmlFor="shopm-contact">Kit contact email</label>
        <p className="kit-hint" style={{ marginBottom: 8 }}>
          {email
            ? 'Where "Ask about this" enquiries are emailed. Leave it blank to stop taking enquiries in the app.'
            : 'Add one and members can tap "Ask about this" on an item. Until then the button is hidden.'}
        </p>
        <div className="shopm-add-row">
          <input id="shopm-contact" className="input" type="email" inputMode="email" autoComplete="email" maxLength={255}
            placeholder="e.g. kit@yourclub.co.uk" value={value} onChange={e => setValue(e.target.value)} />
          <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          {email && <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setEditing(false); setError(''); }}>Cancel</button>}
        </div>
        {!emailsOn && <p className="kit-hint" style={{ marginTop: 8 }}>Enquiries are emailed once Marakin has verified your club. Until then they're listed under Enquiries.</p>}
        {error && <div className="kit-error">{error}</div>}
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Item rows
// ─────────────────────────────────────────────────────────────────────

function ItemRow({ item, first, last, busy, onToggle, onEdit, onAction }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('touchstart', close); };
  }, [menuOpen]);
  const pick = (a) => { setMenuOpen(false); onAction(a); };
  const e = item.enquiries || {};
  const visible = item.is_available !== false;
  return (
    <div className={`shopm-row card ${visible ? '' : 'is-hidden'}`}>
      {item.photos?.length
        ? <KitPhoto url={item.photos[0].url} alt="" className="kit-photo-thumb" Placeholder={ShoppingBag} />
        : item.image_url
          ? <div className="kit-photo kit-photo-thumb"><img src={item.image_url} alt="" /></div>
          : <div className="kit-photo kit-photo-thumb"><ShoppingBag size={28} className="kit-photo-missing" /></div>}
      <div className="shopm-row-main">
        <div className="shopm-row-title">
          <strong className="kit-item-name">{item.name}</strong>
          {!visible && <span className="badge badge-silver">Hidden</span>}
        </div>
        <p className="kit-subline">{[CATEGORY_LABEL[item.category] || item.category, priceText(item.price), item.sizes?.length ? `${item.sizes.length} size${item.sizes.length === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ')}</p>
        {e.total > 0 && (
          <p className="kit-hint">{e.total} enquir{e.total === 1 ? 'y' : 'ies'}{e.open ? ` · ${e.open} open` : ''}{e.sold ? ` · ${e.sold} sold` : ''}</p>
        )}
        <div className="shopm-row-actions">
          <label className="shopm-switch">
            <input type="checkbox" checked={visible} disabled={busy} onChange={() => onToggle(!visible)} />
            <span>Show in shop</span>
          </label>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onEdit} disabled={busy}><Pencil size={14} /> Edit</button>
          <div className="kit-more shopm-more" ref={menuRef}>
            <button type="button" className="kit-more-btn" aria-label={`More options for ${item.name}`} aria-haspopup="menu" aria-expanded={menuOpen}
              onClick={() => setMenuOpen(o => !o)} disabled={busy}><MoreVertical size={18} /></button>
            {menuOpen && (
              <div className="kit-more-menu" role="menu">
                <button type="button" role="menuitem" onClick={() => pick('duplicate')}><Copy size={14} /> Duplicate</button>
                {!first && <button type="button" role="menuitem" onClick={() => pick('up')}><ArrowUp size={14} /> Move up</button>}
                {!last && <button type="button" role="menuitem" onClick={() => pick('down')}><ArrowDown size={14} /> Move down</button>}
                <button type="button" role="menuitem" className="btn-danger-text" onClick={() => pick('delete')}><Trash2 size={14} /> Delete</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Enquiries
// ─────────────────────────────────────────────────────────────────────

const ENQUIRY_FILTERS = [
  { key: 'open', label: 'Open' },
  { key: 'sold', label: 'Sold' },
  { key: 'closed', label: 'Closed' },
  { key: 'all', label: 'All' },
];
const STATUS_BADGE = {
  new: ['badge-gold', 'New'],
  reserved: ['badge-green', 'Reserved'],
  sold: ['badge-green', 'Sold'],
  closed: ['badge-silver', 'Closed'],
};

function EnquiryRow({ clubSlug, enquiry, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [selling, setSelling] = useState(false);
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const e = enquiry;
  const [cls, label] = STATUS_BADGE[e.status] || ['badge-silver', e.status];

  const move = async (status, amountPence) => {
    setBusy(true); setError('');
    try {
      await axios.patch(`/clubs/${clubSlug}/shop/admin/enquiries/${e.id}`, amountPence === undefined ? { status } : { status, amount_pence: amountPence });
      setSelling(false);
      onChanged();
    } catch (err) {
      setError(errText(err, 'Could not update — try again.'));
    } finally { setBusy(false); }
  };
  const startSold = () => {
    const pre = e.status === 'sold' ? e.amount_pence : e.suggested_amount_pence;
    setAmount(pre != null ? (pre / 100).toFixed(2) : '');
    setSelling(true); setError('');
  };
  const confirmSold = (ev) => {
    ev.preventDefault();
    const p = toPence(amount);
    if (p === null) { setError('Enter the amount taken, e.g. 45 or 44.99.'); return; }
    if (p > MAX_AMOUNT_PENCE) { setError('That\'s more than £100,000 — check the amount.'); return; }
    move('sold', p);
  };

  return (
    <div className="shopm-enq card">
      <div className="shopm-row-title">
        <strong className="kit-item-name">{e.item_name}</strong>
        <span className={`badge ${cls}`}>{label}</span>
      </div>
      <p className="kit-subline">
        {[e.size ? `Size ${e.size}` : null, `Qty ${e.quantity}`, priceText(e.unit_price), shortDate(e.created_at)].filter(Boolean).join(' · ')}
        {e.item_removed && ' · no longer in the shop'}
      </p>
      <p className="shopm-enq-who">
        {e.member_name} · <a href={`mailto:${e.member_email}`}>{e.member_email}</a>
      </p>
      {e.note && <p className="shopm-enq-note">{e.note}</p>}
      {e.status === 'sold' && (
        <p className="kit-hint">{penceText(e.amount_pence)} recorded by the club{e.status_changed_by_name ? ` (${e.status_changed_by_name}, ${shortDate(e.status_changed_at)})` : ''}</p>
      )}
      {error && <div className="kit-error">{error}</div>}
      {selling ? (
        <form className="shopm-add-row shopm-sold-row" onSubmit={confirmSold}>
          <label className="label" htmlFor={`sold-${e.id}`}>Amount taken</label>
          <div className="shopm-price">
            <span aria-hidden="true">£</span>
            <input id={`sold-${e.id}`} className="input" inputMode="decimal" autoComplete="off" value={amount} onChange={ev => setAmount(ev.target.value)} />
          </div>
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy}><Check size={14} /> Mark sold</button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelling(false)}>Cancel</button>
        </form>
      ) : (
        <div className="kit-row">
          {e.status === 'new' && <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => move('reserved')}>Reserved</button>}
          {(e.status === 'new' || e.status === 'reserved') && <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={startSold}>Sold…</button>}
          {e.status === 'sold' && <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={startSold}>Change amount</button>}
          {(e.status === 'new' || e.status === 'reserved') && <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => move('closed')}>Close (no sale)</button>}
          {e.status === 'reserved' && <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => move('new')}>Back to new</button>}
          {(e.status === 'sold' || e.status === 'closed') && (
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy}
              onClick={() => { if (e.status !== 'sold' || window.confirm('Reopen this enquiry? The amount recorded will be cleared.')) move('new'); }}>
              <RotateCcw size={14} /> Reopen
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Enquiries({ clubSlug }) {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('open');
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['shop-admin', clubSlug, 'enquiries', filter],
    queryFn: () => axios.get(`/clubs/${clubSlug}/shop/admin/enquiries`, { params: { status: filter } }).then(r => r.data),
    placeholderData: (prev) => prev,
  });
  const counts = data?.counts || {};
  const countFor = (k) => (k === 'open' ? (counts.new || 0) + (counts.reserved || 0)
    : k === 'all' ? Object.values(counts).reduce((a, b) => a + b, 0) : counts[k] || 0);
  const refresh = () => qc.invalidateQueries({ queryKey: ['shop-admin', clubSlug] });

  return (
    <div className="shopm-enquiries">
      <div className="pill-group">
        {ENQUIRY_FILTERS.map(f => (
          <button type="button" key={f.key} className={`pill-btn ${filter === f.key ? 'active' : ''}`} onClick={() => setFilter(f.key)}>
            {f.label}{countFor(f.key) > 0 && <span className="tab-count">{countFor(f.key)}</span>}
          </button>
        ))}
      </div>
      {isLoading && <div className="page-loading"><div className="spinner" /></div>}
      {isError && <div className="kit-error">{errText(error, 'Could not load enquiries.')}</div>}
      {data && data.enquiries.length === 0 && (
        <div className="empty-state">
          <Inbox size={28} style={{ opacity: 0.4 }} />
          <p>{filter === 'open' ? 'No open enquiries. When a member taps Ask about this, it lands here.' : 'Nothing here.'}</p>
        </div>
      )}
      {data?.enquiries.map(e => <EnquiryRow key={e.id} clubSlug={clubSlug} enquiry={e} onChanged={refresh} />)}
      {data?.truncated && <p className="kit-hint">Showing the newest 200 — narrow it down with the filters.</p>}
      {data && <p className="kit-hint">Amounts are recorded by the club — no money goes through Marakin yet. Enquiries are kept for {data.keep_months} months.</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// The screen
// ─────────────────────────────────────────────────────────────────────

export default function ShopManager({ clubSlug, TabHead }) {
  const qc = useQueryClient();
  // ?view=enquiries (the enquiry email's button) opens the Enquiries list.
  const [searchParams] = useSearchParams();
  const [view, setView] = useState(searchParams.get('view') === 'enquiries' ? 'enquiries' : 'items'); // items | enquiries
  const [editing, setEditing] = useState(null); // null | 'new' | item
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const topRef = useRef(null);

  const { data, isLoading, isError, error: loadError } = useQuery({
    queryKey: ['shop-admin', clubSlug, 'items'],
    queryFn: () => axios.get(`/clubs/${clubSlug}/shop/admin`).then(r => r.data),
  });
  const items = useMemo(() => data?.items || [], [data]);
  const deleted = data?.deleted || [];
  const newEnquiries = data?.enquiry_counts?.new || 0;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['shop-admin', clubSlug] });
    qc.invalidateQueries({ queryKey: ['products', clubSlug] }); // the member Shop + the Shop pill count
  };
  const say = (msg) => { setNotice(msg); setError(''); };
  const scrollTop = () => topRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });

  const run = async (id, fn, okMsg) => {
    setBusyId(id); setError(''); setNotice('');
    try {
      await fn();
      if (okMsg) setNotice(okMsg);
    } catch (err) {
      setError(errText(err, 'Something went wrong — try again.'));
    } finally {
      setBusyId(null);
      refresh();
    }
  };
  const base = `/clubs/${clubSlug}/shop/admin/items`;
  const toggle = (item, show) => run(item.id, () => axios.patch(`${base}/${item.id}`, { is_available: show }),
    show ? `${item.name} is in the shop.` : `${item.name} is hidden from members.`);
  const onAction = (item, a) => {
    if (a === 'duplicate') return run(item.id, () => axios.post(`${base}/${item.id}/duplicate`), `Copied as hidden: ${item.name} (copy).`);
    if (a === 'up' || a === 'down') return run(item.id, () => axios.post(`${base}/${item.id}/move`, { direction: a }));
    if (a === 'delete') {
      if (!window.confirm(`Delete ${item.name}? It leaves the shop now. You can restore it for 30 days.`)) return undefined;
      return run(item.id, () => axios.delete(`${base}/${item.id}`), `${item.name} deleted — restore it below within 30 days if that was a mistake.`);
    }
    return undefined;
  };
  const restore = (item) => run(item.id, () => axios.post(`${base}/${item.id}/restore`), `${item.name} is back.`);

  const Head = TabHead || (({ children }) => <div className="admin-tab-head">{children}</div>);

  if (editing) {
    return (
      <div className="admin-section" ref={topRef}>
        <Head icon={ShoppingBag} title="Shop" count={items.length} />
        <ItemForm clubSlug={clubSlug} item={editing === 'new' ? null : editing}
          onCancel={() => { setEditing(null); scrollTop(); }}
          onDone={(msg) => { setEditing(null); say(msg); refresh(); scrollTop(); }} />
      </div>
    );
  }

  return (
    <div className="admin-section shopm" ref={topRef}>
      <Head icon={ShoppingBag} title="Shop" count={items.length}>
        {view === 'items' && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => { setEditing('new'); setNotice(''); }}>
            <Plus size={14} /> Add item
          </button>
        )}
      </Head>

      <div className="pill-group shopm-views">
        <button type="button" className={`pill-btn ${view === 'items' ? 'active' : ''}`} onClick={() => setView('items')}>Items</button>
        <button type="button" className={`pill-btn ${view === 'enquiries' ? 'active' : ''}`} onClick={() => setView('enquiries')}>
          Enquiries{newEnquiries > 0 && <span className="tab-count">{newEnquiries}</span>}
        </button>
      </div>

      {isLoading && <div className="page-loading"><div className="spinner" /></div>}
      {isError && <div className="kit-error">{errText(loadError, 'Could not load the shop.')}</div>}

      {data && (
        <KitContactCard clubSlug={clubSlug} email={data.settings?.contact_email} emailsOn={!!data.settings?.emails_on}
          onSaved={() => { refresh(); qc.invalidateQueries({ queryKey: ['club', clubSlug] }); }} />
      )}

      {notice && (
        <div className="kit-note kit-notice" role="status">
          <span>{notice}</span>
          <button type="button" className="kit-notice-close" aria-label="Dismiss" onClick={() => setNotice('')}><X size={14} /></button>
        </div>
      )}
      {error && <div className="kit-error">{error}</div>}

      {view === 'enquiries' && <Enquiries clubSlug={clubSlug} />}

      {view === 'items' && data && (
        <>
          {items.length === 0 && (
            <div className="empty-state kit-empty">
              <ShoppingBag size={28} style={{ opacity: 0.4 }} />
              <p>Your shop is empty. Add your kit and merchandise — it shows in the app straight away.</p>
              <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditing('new')}><Plus size={14} /> Add item</button>
            </div>
          )}
          <div className="shopm-list">
            {items.map((item, i) => (
              <ItemRow key={item.id} item={item} first={i === 0} last={i === items.length - 1} busy={busyId === item.id}
                onToggle={(show) => toggle(item, show)} onEdit={() => { setEditing(item); setNotice(''); }}
                onAction={(a) => onAction(item, a)} />
            ))}
          </div>

          {deleted.length > 0 && (
            <section className="shopm-deleted">
              <h3 className="kit-section-title">Deleted</h3>
              <p className="kit-hint">Not in the shop. Restore within {data.limits?.restore_days || 30} days, then they're removed for good.</p>
              {deleted.map(item => (
                <div key={item.id} className="shopm-row card is-hidden">
                  <div className="shopm-row-main">
                    <strong className="kit-item-name">{item.name}</strong>
                    <p className="kit-hint">Removed for good on {shortDate(item.restore_until)}</p>
                  </div>
                  <button type="button" className="btn btn-ghost btn-sm" disabled={busyId === item.id} onClick={() => restore(item)}><RotateCcw size={14} /> Restore</button>
                </div>
              ))}
            </section>
          )}
        </>
      )}
    </div>
  );
}
